/**
 * Turns an imported spec's row axis into a studio line set, so the rows shelf shows the imported rows
 * and its pencil opens the line-set editor. `selector.elements` is already the deck's pre-resolved,
 * ordered member list.
 */

import type { ReportSpec, RowStyle } from 'src/components/star-grid/api/reportEngineTypes';
import type { StudioField, StudioLineEntry, StudioLineSet } from 'src/components/star-grid/reportStudio.types';

/**
 * Drops the deck's `_str` suffix from a row name.
 *
 * Section headings are named `Units_str`, `Revenue_str`, … — a TM1 convention marking a string element,
 * not something to print. Prophecy shows `Units`; the suffix only leaked because a heading is not an
 * ancestor in the closure, so it has no `ancestor_display` to fall back to.
 */
export const stripStrSuffix = (name: string): string => String(name ?? '').replace(/_str$/i, '');

/** Label for a line set the studio synthesised from an import, rather than one the deck shipped. */
export const IMPORTED_LINE_SET_VERSION = 'imported';

/**
 * One line, carrying the presentation the REPORT will render.
 *
 * `style` comes from the deck's own row-format attribute (`row_styles` on the import payload). Without
 * it every imported line was flush-left and unbolded, so the line-set editor could not show the
 * statement's shape — a heading looked like a detail row and an indented row looked top-level.
 */
const entry = (label: string, index: number, style?: RowStyle, display?: string): StudioLineEntry => {
  const blank = !label.trim() || style?.spacer === true;
  return {
    id: `imported-line-${index}`,
    // A deck row with no member name — or one the deck marks as a spacer — is a blank separator the
    // deck DRAWS, so it stays a spacer rather than becoming a member line with an empty label.
    kind: blank ? 'spacer' : style?.heading ? 'section' : 'member',
    // The REPORT display name when the closure states one (`UNITS - Echo` prints as `Echo`), else the
    // element name. `label` is what the grid renders, so seeding it here is what makes the editor and
    // the report agree.
    label: blank ? label : display?.trim() || stripStrSuffix(label),
    ...(blank ? {} : { member_id: label }),
    ...(style?.indent ? { indent: style.indent } : {}),
    ...(style?.bold ? { emphasis: 'bold' as const } : {})
  };
};

/**
 * The imported row members, in the deck's order. Empty when the screen states none — a spec whose
 * rows come from a drilldown or a subset reference carries no element list, and inventing lines for
 * it would put rows in the report the deck never had.
 */
export const importedRowMembers = (spec: Pick<ReportSpec, 'row_axis'> | undefined): readonly string[] => {
  const selector = spec?.row_axis?.selector;
  const elements = selector?.elements ?? [];
  return elements.filter((value): value is string => typeof value === 'string');
};

/**
 * A line set carrying the imported report's rows, or `undefined` when there is nothing to seed from.
 *
 * `editable` so the studio lets the rows be reordered, bolded and indented — the whole point of
 * landing on the line-set editor rather than a member picker.
 */
export const importedRowLineSet = (
  spec: Pick<ReportSpec, 'row_axis' | 'row_styles' | 'row_displays'> | undefined,
  label: string
): StudioLineSet | undefined => {
  const members = importedRowMembers(spec);
  if (members.length === 0) return undefined;
  return {
    id: `imported-${spec?.row_axis?.dimension ?? 'rows'}`,
    label,
    version: IMPORTED_LINE_SET_VERSION,
    binding_id: String(spec?.row_axis?.dimension ?? ''),
    editable: true,
    // Styles are aligned to the element list by INDEX, and looked up by member as a fallback: the
    // engine emits one style per row in the deck's own order.
    entries: members.map((member, index) => entry(member, index, styleFor(spec, member, index), spec?.row_displays?.[member]))
  };
};

/** The style for one row: by position first, falling back to a member match. */
const styleFor = (spec: Pick<ReportSpec, 'row_styles'> | undefined, member: string, index: number): RowStyle | undefined => {
  const styles = spec?.row_styles;
  if (!styles || styles.length === 0) return undefined;
  const positional = styles[index];
  if (positional && positional.member === member) return positional;
  return styles.find((style) => style.member === member) ?? positional;
};

/** The rows field pointing at that line set, so the shelf renders it and the pencil opens it. */
export const importedRowField = (set: StudioLineSet): StudioField => ({
  id: `imported-row-${set.id}`,
  binding_id: set.binding_id,
  // `members` is required on every selection and is the member list for MEMBER-kind selections; a
  // line-set selection carries its rows in the set itself, so it is empty here rather than omitted.
  selection: { kind: 'line_set', reference_id: set.id, members: [] }
});
